import { Article, Category } from '@wix/answers-api';
import {
  pageClick,
  wasItHelpfulView,
} from '@wix/bi-logger-customer-care-data/v2';
import {
  helpCenterBannerView,
  helpCenterBannerClick,
  submitWasItHelpfulForm,
} from '@wix/bi-logger-new-help-center/v2';
import { useRouter } from 'next/router';
import { useState, useEffect, useContext } from 'react';

import { BI } from '../constants';
import { mapPageTypeToSourceName, mapArticleTypeToItemType } from './bi';
import { stripHTML } from './text';
import { Context } from '../context';
import { useBI } from '../hooks/useBI';
import { PageType, PanelType, PanelAction } from '../types';

export const useArticlePageBi = (article: Article) => {
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();
  const { sessionBI } = useContext(Context);
  const [wasArticleHelpfulViewedSent, setWasArticleHelpfulViewedSent] =
    useState(false);

  useEffect(() => {
    setWasArticleHelpfulViewedSent(false);
  }, [article.id]);

  const breadCrumbLinkClick = async (
    category: Category,
    clickedItemOrder?: number
  ) => {
    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(PageType.Article),
        item_id: article.id,
        item_type: mapArticleTypeToItemType(article.type),
        clicked_item_type: BI.CLICKED_ITEM_TYPES.BREADCRUMBS,
        clicked_text: category.name,
        clicked_url: category.url,
        clicked_item_id: category.id,
        clicked_item_order: clickedItemOrder?.toString(),
        is_page_content_click: false,
      })
    );
  };

  const homePageClick = async (clickedText: string) => {
    if (window) {
      await sendBIEvent(
        pageClick({
          source_name: mapPageTypeToSourceName(PageType.Article),
          item_id: article.id,
          item_type: mapArticleTypeToItemType(article.type),
          clicked_item_type: BI.CLICKED_ITEM_TYPES.BREADCRUMBS,
          clicked_text: clickedText,
          clicked_url: `${window.location.origin}/${locale}`,
          clicked_item_order: '1',
          clicked_item_id: undefined,
          is_page_content_click: false,
        })
      );
    }
  };

  const articleIsBeingAbTestedView = async (bannerText: string) => {
    if (wasArticleHelpfulViewedSent) {
      return;
    } else {
      await sendBIEvent(
        helpCenterBannerView({
          banner_text: stripHTML(bannerText),
          banner_name: BI.BANNER_NAME.ARTICLE_IN_AB_TEST,
          has_cta: true,
          source_name: mapPageTypeToSourceName(PageType.Article),
          is_resolved: false,
          kb_lang: locale,
          item_type: mapArticleTypeToItemType(article.type),
          item_id: article.id,
          banner_language: locale,
        })
      );
      setWasArticleHelpfulViewedSent(true);
    }
  };

  const articleIsBeingAbTestedClick = async (
    bannerText: string,
    clickedText: string,
    clickedUrl: string
  ) => {
    await sendBIEvent(
      helpCenterBannerClick({
        clicked_text: clickedText,
        banner_text: stripHTML(bannerText),
        banner_name: BI.BANNER_NAME.ARTICLE_IN_AB_TEST,
        clicked_item_type: BI.CLICKED_ITEM_TYPES.LINK,
        source_name: mapPageTypeToSourceName(PageType.Article),
        is_resolved: false,
        kb_lang: locale,
        item_type: mapArticleTypeToItemType(article.type),
        item_id: article.id,
        banner_language: locale,
        clicked_url: `${location.origin}/${locale}${clickedUrl}`,
      })
    );
  };
  const panelTrack = async (panelType: PanelType, action: PanelAction) => {
    let clickedItemType: string;

    if (panelType === PanelType.ASSISTANT) {
      clickedItemType =
        action === PanelAction.COLLAPSE
          ? BI.CLICKED_ITEM_TYPES.ASSISTANT_PANEL_COLLAPSE
          : BI.CLICKED_ITEM_TYPES.ASSISTANT_PANEL_EXPAND;
    } else {
      clickedItemType =
        action === PanelAction.COLLAPSE
          ? BI.CLICKED_ITEM_TYPES.ANCHORS_PANEL_COLLAPSE
          : BI.CLICKED_ITEM_TYPES.ANCHORS_PANEL_EXPAND;
    }

    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(PageType.Article),
        item_id: article.id,
        item_type: mapArticleTypeToItemType(article.type),
        clicked_item_type: clickedItemType,
        kb_lang: locale,
        self_service_session_id: sessionBI?.selfServiceSessionId || undefined,
        is_page_content_click: false,
      })
    );
  };

  const articleSummaryTrack = async (action: PanelAction) => {
    const clickedItemType =
      action === PanelAction.COLLAPSE
        ? BI.CLICKED_ITEM_TYPES.ARTICLE_SUMMARY_COLLAPSE
        : BI.CLICKED_ITEM_TYPES.ARTICLE_SUMMARY_EXPAND;

    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(PageType.Article),
        item_id: article.id,
        item_type: mapArticleTypeToItemType(article.type),
        clicked_item_type: clickedItemType,
        kb_lang: locale,
        self_service_session_id: sessionBI?.selfServiceSessionId || undefined,
        is_page_content_click: false,
      })
    );
  };

  const suggestedQuestionTrack = async (
    questionText: string,
    questionOrder: number
  ) => {
    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(PageType.Article),
        item_id: article.id,
        item_type: mapArticleTypeToItemType(article.type),
        clicked_item_type:
          BI.CLICKED_ITEM_TYPES.ARTICLE_ASSISTANT_SUGGESTED_QUESTION,
        clicked_text: questionText,
        clicked_item_order: questionOrder.toString(),
        kb_lang: locale,
        self_service_session_id: sessionBI?.selfServiceSessionId || undefined,
        is_page_content_click: false,
      })
    );
  };

  const chatNotificationTrack = async () => {
    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(PageType.Article),
        item_id: article.id,
        item_type: mapArticleTypeToItemType(article.type),
        clicked_item_type:
          BI.CLICKED_ITEM_TYPES.ARTICLE_ASSISTANT_CHAT_NOTIFICATION,
        kb_lang: locale,
        self_service_session_id: sessionBI?.selfServiceSessionId || undefined,
        is_page_content_click: false,
      })
    );
  };

  const articleAssistantFreeTextTrack = async (enteredText: string) => {
    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(PageType.Article),
        item_id: article.id,
        item_type: mapArticleTypeToItemType(article.type),
        clicked_item_type: BI.CLICKED_ITEM_TYPES.ARTICLE_ASSISTANT_FREE_TEXT,
        clicked_text: enteredText,
        kb_lang: locale,
        self_service_session_id: sessionBI?.selfServiceSessionId || undefined,
        is_page_content_click: false,
      })
    );
  };

  const assistantPanelVisibleTrack = async () => {
    await sendBIEvent(
      wasItHelpfulView({
        source_name: mapPageTypeToSourceName(PageType.Article),
        item_id: article.id,
        kb_lang: locale,
        self_service_session_id: sessionBI?.selfServiceSessionId || undefined,
        item_name: BI.CLICKED_ITEM_TYPES.ARTICLE_ASSISTANT,
      })
    );
  };

  const unhelpfulFeedbackTrack = async (
    freeText = '',
    clickedText = '',
    clickedItemOrder = ''
  ) => {
    await sendBIEvent(
      submitWasItHelpfulForm({
        source_name: mapPageTypeToSourceName(PageType.Article),
        item_id: article.id,
        kb_lang: locale,
        self_service_session_id: sessionBI?.selfServiceSessionId || undefined,
        thumbs_up: false,
        feedback_details:
          BI.FEEDBACK_DETAILS.ARTICLE_ASSISTANT_UNHELPFUL_REASON,
        free_text: freeText,
        clicked_text: clickedText,
        clicked_item_order: clickedItemOrder,
      })
    );
  };

  const unhelpfulFreeTextPageClick = async (freeText: string) => {
    await sendBIEvent(
      pageClick({
        source_name: mapPageTypeToSourceName(PageType.Article),
        item_id: article.id,
        item_type: mapArticleTypeToItemType(article.type),
        clicked_item_type:
          BI.CLICKED_ITEM_TYPES.ARTICLE_ASSISTANT_UNHELPFUL_FREE_TEXT,
        clicked_text: freeText,
        kb_lang: locale,
        self_service_session_id: sessionBI?.selfServiceSessionId || undefined,
      })
    );
  };

  return {
    breadCrumbLinkClick,
    homePageClick,
    articleIsBeingAbTestedView,
    articleIsBeingAbTestedClick,
    panelTrack,
    articleSummaryTrack,
    suggestedQuestionTrack,
    chatNotificationTrack,
    articleAssistantFreeTextTrack,
    assistantPanelVisibleTrack,
    unhelpfulFeedbackTrack,
    unhelpfulFreeTextPageClick,
  };
};
